import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import rawContent from '../variants/variants.json';

// ─── Context ──────────────────────────────────────────────────────────────────
const ContentContext = createContext(null);
const VARIANT_TAG = "440artists_variant";
const VARIANT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const VARIANT_STORAGE_KEY = "440artists_variant";
const FORCED_VARIANT = "a";

const canUseLocalStorage = () => {
    try {
        if (typeof window === "undefined" || !window.localStorage) return false;
        const testKey = "__440artists_storage_test__";
        window.localStorage.setItem(testKey, "1");
        window.localStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
};

const setStoredVariant = (value) => {
    if (!canUseLocalStorage()) return;
    window.localStorage.setItem(VARIANT_STORAGE_KEY, value);
};

const saveVariant = (name, value) => {
    const encodedName = encodeURIComponent(name);
    const encodedValue = encodeURIComponent(value);
    const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
    const secureFlag = isHttps ? "; Secure" : "";

    // Use cross-site compatible cookie attributes for iframe scenarios.
    document.cookie = `${encodedName}=${encodedValue}; Max-Age=${VARIANT_COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=None${secureFlag}; Partitioned`;

    // Fallback for browsers/environments where third-party cookies are blocked.
    setStoredVariant(value);
};

export const getVariant = (name) => {
    const encodedName = `${encodeURIComponent(name)}=`;
    const cookie = document.cookie
        .split(';')
        .map((entry) => entry.trim())
        .find((entry) => entry.startsWith(encodedName));

    if (!cookie) return null;

    return decodeURIComponent(cookie.substring(encodedName.length));
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export function ContentProvider({ children }) {
    /**
     * Section-based state: each top-level key in content.json becomes
     * its own state slice so updates to one section don't re-render siblings.
     */
    const { search } = useLocation();

    const getInitialVariant = () => FORCED_VARIANT;

    const [variant, setVariant] = useState(getInitialVariant);
    const [theme, setTheme] = useState(() => rawContent[getInitialVariant()] || rawContent["a"]);
    useEffect(() => {
        saveVariant(VARIANT_TAG, FORCED_VARIANT);

        if (variant !== FORCED_VARIANT) {
            setVariant(FORCED_VARIANT);
        }

        const params = new URLSearchParams(window.location.search);
        params.set("v", FORCED_VARIANT);
        const cleanUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", cleanUrl);
    }, [search, variant]);


    // --- Set theme based on variant ---
    useEffect(() => {
        if (variant && rawContent[variant]) {
            setTheme(rawContent[variant]);
        }
    }, [variant]);

    const value = {
        variant,
        header: theme?.header || rawContent["a"].header,
        hero: theme?.hero || rawContent["a"].hero,
        footer: theme?.footer || rawContent["a"].footer,
        howItWorks: theme?.howItWorks || rawContent["a"].howItWorks,
        ourNetwork: theme?.ourNetwork || rawContent["a"].ourNetwork,
        Benefits: theme?.Benefits || rawContent["a"].Benefits,
        pricing: theme?.pricing || rawContent["a"].pricing,
        faq: theme?.faq || rawContent["a"].faq,
        whatYouGet: theme?.whatYouGet || rawContent["a"].whatYouGet,
        theRoster: theme?.theRoster || rawContent["a"].theRoster,
        artistStories: theme?.artistStories || rawContent["a"].artistStories,
    };

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
}

// ─── Section hooks ─────────────────────────────────────────────────────────────
/** Returns only the header slice of content — Header component uses this. */
export function useHeaderContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useHeaderContent must be used inside <ContentProvider>');
    return ctx.header;
}

/** Returns only the hero slice of content — Hero component uses this. */
export function useHeroContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useHeroContent must be used inside <ContentProvider>');
    return ctx.hero;
}

/** Returns only the footer slice of content — Footer component uses this. */
export function useFooterContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useFooterContent must be used inside <ContentProvider>');
    return ctx.footer;
}

/** Returns only the offers slice of content — WhatWeOffer component uses this. */
export function useOffersContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useOffersContent must be used inside <ContentProvider>');
    return ctx.offers;
}

/** Returns only the "How It Works" slice of content. */
export function useHowItWorksContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useHowItWorksContent must be used inside <ContentProvider>');
    return ctx.howItWorks;
}

/** Returns only the "Our Network" slice of content. */
export function useOurNetworkContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useOurNetworkContent must be used inside <ContentProvider>');
    return ctx.ourNetwork;
}

/** Returns only the "Media Ecosystem" slice of content. */
export function useBenefitsContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useBenefitsContent must be used inside <ContentProvider>');
    return ctx.Benefits;
}

/** Returns only the "Featured Artists" slice of content. */
export function useFeaturedArtistsContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useFeaturedArtistsContent must be used inside <ContentProvider>');
    return ctx.featuredArtists;
}

/** Returns only the "Pricing" slice of content. */
export function usePricingContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('usePricingContent must be used inside <ContentProvider>');
    return ctx.pricing;
}

/** Returns only the "FAQ" slice of content. */
export function useFaqContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useFaqContent must be used inside <ContentProvider>');
    return ctx.faq;
}

/** Returns only the "What You Get" slice of content. */
export function useWhatYouGetContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useWhatYouGetContent must be used inside <ContentProvider>');
    return ctx.whatYouGet;
}

/** Returns only the "The Roster" slice of content. */
export function useArtistRosterContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useArtistRosterContent must be used inside <ContentProvider>');
    return ctx.theRoster;
}

/** Returns only the "Artist Stories" slice of content. */
export function useArtistStoriesContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useArtistStoriesContent must be used inside <ContentProvider>');
    return ctx.artistStories;
}

export default ContentContext;
